import React, { useState, useRef } from 'react';
import DataList from '../DataList';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import ApiConfigFactory from '../../configurations/ApiConfigFactory';
import { configurePatientVisitForm } from '../../configurations/patientVisitFormConfig';

const BASE_URL = process.env.REACT_APP_SERVER_BASE_URL || 'http://localhost:5002';

const PatientVisitList = ({ patient: patientProp }) => {
    const location = useLocation();
    const patient = patientProp || location.state?.patient || {};

    // --- Referral prompt state ---
    const [pendingReferral, setPendingReferral] = useState(null);
    const referralPromiseRef = useRef(null);

    const checkPendingReferral = async () => {
        if (!patient?.patient_id) return null;
        try {
            const res = await axios.get(
                `${BASE_URL}/hospital-portal/api/patients/${patient.patient_id}/referrals?status=pending&type=referral`
            );
            const referrals = res.data || [];
            if (referrals.length === 0) return null;
            return new Promise((resolve) => {
                setPendingReferral(referrals[0]);
                referralPromiseRef.current = resolve;
            });
        } catch {
            return null;
        }
    };

    const isReferralSameDay = (referral) => {
        const sourceVisitDate = referral.SourceVisit?.visit_date;
        if (!sourceVisitDate) return false;
        const todayStr = new Date().toISOString().split('T')[0];
        return new Date(sourceVisitDate).toISOString().split('T')[0] === todayStr;
    };

    const handleReferralPromptYes = async () => {
        const referral = pendingReferral;
        setPendingReferral(null);
        try {
            await axios.patch(
                `${BASE_URL}/hospital-portal/api/patients/referrals/${referral.id}`,
                { status: 'completed' }
            );
        } catch { /* non-blocking */ }

        const today = new Date();
        const referredDate = today.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        const symptomsText = [
            `Referred by: ${referral.referred_by_doctor}`,
            `Referred Date: ${referredDate}`,
            referral.notes ? `Referral Note: ${referral.notes}` : null,
        ].filter(Boolean).join('\n');

        const prefill = {
            patient_id: referral.patient_id,
            visit_reason: 'referral',
            assigned_doctor: referral.referred_to_doctor,
            branch: referral.branch || '',
            symptoms: symptomsText,
            // Treatment tab is hidden for some roles; keep this prefilled value on create only.
            __keepHiddenOnCreate: ['symptoms'],
        };

        // Copy vitals from source visit when the referral is happening same-day
        if (isReferralSameDay(referral) && referral.SourceVisit) {
            const sv = referral.SourceVisit;
            if (sv.temperature != null) prefill.temperature = sv.temperature;
            if (sv.blood_pressure_systolic != null) prefill.blood_pressure_systolic = sv.blood_pressure_systolic;
            if (sv.blood_pressure_diastolic != null) prefill.blood_pressure_diastolic = sv.blood_pressure_diastolic;
            if (sv.pulse != null) prefill.pulse = sv.pulse;
            if (sv.weight != null) prefill.weight = sv.weight;
            if (sv.spo2 != null) prefill.spo2 = sv.spo2;
        }

        referralPromiseRef.current?.(prefill);
        referralPromiseRef.current = null;
    };

    const handleReferralPromptNo = () => {
        setPendingReferral(null);
        referralPromiseRef.current?.(null);
        referralPromiseRef.current = null;
    };

    const dlConfig = new ApiConfigFactory(
        'patients/visits',
        'All Visits',
        'patient_id',
        patient?.patient_id
    );

    dlConfig.title = patient?.patient_id
        ? `Visit for Patient: ${patient?.patient_first_name} ${patient?.patient_last_name}`
        : 'All Visits';
    dlConfig.formTitle = patient?.patient_id
        ? `Visit for Patient: ${patient?.patient_first_name} ${patient?.patient_last_name}`
        : '';
    dlConfig.allowAdd = !!patient?.patient_id;
    dlConfig.path = '/patient-visits';
    dlConfig.useLocalCaching = (patient && Object.keys(patient).length > 0) ? false : true;

    if (patient?.patient_id) {
        dlConfig.onBeforeAdd = checkPendingReferral;
    }

    dlConfig.defaultSortFieldId = 'visit_id';

    configurePatientVisitForm(dlConfig, { patient, mode: 'full' });

    dlConfig
        .addColumn({ id: 'visit_id', name: 'Visit ID', selector: (row) => row.visit_id, type: 'number', sortable: true, identifier: true, width: '100px', filterable: true, filterSelector: 'visit_id' })
        .addColumn({
            name: 'Patient',
            type: 'link',
            label: (row) => `${row.Patient?.patient_first_name} ${row.Patient?.patient_last_name}`,
            path: '/patient-card',
            getState: (row) => ({ patient: row.Patient }),
            selector: (row) => `${row.Patient?.patient_first_name} ${row.Patient?.patient_last_name}`,
            width: '200px',
        })
        .addColumn({ name: 'Patient #', selector: (row) => row.Patient.patient_number, type: 'text', sortable: true, filterable: true, filterSelector: 'Patient.patient_number', width: '120px' })
        .addColumn({ name: 'Type', selector: (row) => row.Patient.patient_type, type: 'text', sortable: true, width: '100px' })
        .addColumn({ name: 'Visit Date', selector: (row) => row.visit_date, type: 'datetime', sortable: true })
        .addColumn({ name: 'Invoiced', selector: (row) => row.invoiced, type: 'boolean', sortable: true, filterable: true, width: '90px' })
        .addColumn({ name: 'Assigned Doctor', selector: (row) => row.assigned_doctor, sortable: true, filterable: true, filterSelector: 'assigned_doctor', width: '150px' })
        .addColumn({ name: 'Branch', selector: (row) => row.branch, sortable: true, filterable: true, filterSelector: 'branch', width: '100px' });

    return (
        <>
            <DataList apiConfig={dlConfig} columns={dlConfig.columns} />

            {pendingReferral && (
                <div className="lab-request-modal-overlay" onClick={handleReferralPromptNo}>
                    <div className="lab-request-modal-content referral-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="referral-modal-header">
                            <h3>Pending Referral</h3>
                            <button className="referral-modal-close" onClick={handleReferralPromptNo} type="button" aria-label="Close">&times;</button>
                        </div>
                        <div className="referral-modal-patient">
                            This patient has a pending referral to <strong>{pendingReferral.referred_to_doctor}</strong>.
                            {pendingReferral.notes && (
                                <p style={{ marginTop: 8, marginBottom: 0, fontSize: 13, color: '#475569' }}>
                                    <em>{pendingReferral.notes}</em>
                                </p>
                            )}
                        </div>
                        {!isReferralSameDay(pendingReferral) && (
                            <p style={{ fontSize: 13, color: '#dc2626', fontWeight: 600, margin: '10px 0 4px' }}>
                                New vitals are required for this visit.
                            </p>
                        )}
                        <p style={{ fontSize: 14, color: '#374151', margin: '12px 0 16px' }}>
                            Is this visit related to that referral?
                        </p>
                        <div className="referral-modal-actions">
                            <button type="button" className="referral-btn-cancel" onClick={handleReferralPromptNo}>No — New Visit</button>
                            <button type="button" className="referral-btn-submit" onClick={handleReferralPromptYes}>Yes — Use Referral</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PatientVisitList;
